import React, {useCallback, useEffect, useRef, useState} from "react";
import loadScript from "@braintree/asset-loader/dist/load-script";
import {displayName, INTERNAL_ERROR_TYPE, isoLocales, retrieveCheckout, Payments} from "../PaymentController";
import {getShopId} from "../../getShopId";
import braintree, {Client} from "braintree-web";
import {isCart, IUnsafeCartContext, useUnsafeCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import {ButtonPropType} from "../paypal/helpers/paypalTypes";
import {useTranslation} from "react-i18next";
import {Cart, Checkout, IAddressData} from "@connox/typescript-library";
import {IPaymentTransaction} from "@connox/typescript-library/types/checkout/requestHandlers/CheckoutRequestHandler";
import ApplePayPaymentRequest = ApplePayJS.ApplePayPaymentRequest;
import ApplePayValidateMerchantEvent = ApplePayJS.ApplePayValidateMerchantEvent;
import ApplePayShippingContactSelectedEvent = ApplePayJS.ApplePayShippingContactSelectedEvent;
import ApplePayShippingMethod = ApplePayJS.ApplePayShippingMethod;
import ApplePayShippingMethodSelectedEvent = ApplePayJS.ApplePayShippingMethodSelectedEvent;
import ApplePayPaymentMethodSelectedEvent = ApplePayJS.ApplePayPaymentMethodSelectedEvent;
import ApplePayPaymentAuthorizedEvent = ApplePayJS.ApplePayPaymentAuthorizedEvent;
import ApplePayCouponCodeChangedEvent = ApplePayJS.ApplePayCouponCodeChangedEvent;
import {
  appleAddressAdapter,
  asyncCouponCodeChange,
  asyncPutShippingCountry,
  asyncPutShippingMethod,
  dbqs,
  findAuthorizeErrors,
  getProductData,
  positionsToLineItems,
  putPaymentMethod,
  setAPayDataOnViewState,
} from "./applePayHelperFunctions";
import {PayPalLoadingSpinner} from "../paypal/BraintreeButton";
import {CurrencyCode} from "@connox/typescript-library/types/cart/CurrencyCode";
import {decideCartContent, restoreCart} from "../decideCartContent";
import {expressPaymentsError} from "../../../../helpers/errorHandling";
import {DEFAULT_ERROR_MESSAGE} from "../../../checkout/Checkout";
import {updateUrlParams} from "../../../cart/CartIcon/CartIconPortal";
import {Exponea} from "../../../../tracking/exponea";

export type ErrorMagicAdditionalType =
  | IPaymentTransaction
  | ApplePayCouponCodeChangedEvent
  | ApplePayPaymentAuthorizedEvent
  | ApplePayPaymentMethodSelectedEvent
  | ApplePayShippingContactSelectedEvent
  | ApplePayShippingMethodSelectedEvent
  | ApplePayValidateMerchantEvent;

export type PaymentStateType = {
  paymentRequest: ApplePayPaymentRequest;
  applePayClient: braintree.ApplePay;
};

export type CartStuffType = {
  place?: "cart" | "pdp";
};

/* `handleError()` and `onReturn()` will be directly handled in the component with the cart context ref */
type ApplePayButtonLoaderProps = Omit<ButtonPropType, "onReturn" | "handleError"> &
  CartStuffType & {supportedVersion: number};

// region declares to work with `ApplePaySession`

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "apple-pay-button": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          buttonstyle: string;
          type: string;
          locale: string;
        },
        HTMLElement
      >;
    }
  }
  interface Window {
    ApplePaySession: ApplePaySession | undefined;
  }
}

// endregion declares to work with `ApplePaySession`

// Will be deleted on end of debugging and console.log removal!
/* eslint-disable no-console */

const ApplePayButtonLoader: React.FC<ApplePayButtonLoaderProps> = ({isBlocked, supportedVersion, place = "cart"}) => {
  const context = useUnsafeCartContext();
  const contextRef = useRef<IUnsafeCartContext>();
  contextRef.current = context;
  const {t} = useTranslation(["translations", "errorcodes"]);
  const elemRef = useRef<HTMLElement | null>(null);
  const checkoutRef = useRef<Checkout>();
  const cartRef = useRef<Cart>();
  const [paymentData, setPaymentData] = useState<PaymentStateType>();
  const paymentDataRef = useRef(paymentData);
  const shippingMethodsRef = useRef<ApplePayShippingMethod[] | undefined>(undefined);
  paymentDataRef.current = paymentData;
  const isBlockedRef = useRef<boolean>(isBlocked);
  isBlockedRef.current = isBlocked;
  const [alreadyLoading, setAlreadyLoading] = useState<boolean>(false);
  const [itemId, setItemId] = useState<number>();

  // Just a trigger to then assign the onclick handler _natively_ to the `elementRef.current`.
  // See `if` statement right before the `return`
  const [, setScriptLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (!context?.braintreeData || !context.countries.length) return;
    if (alreadyLoading) return;

    setAlreadyLoading(true);

    async function loadData(client: Client) {
      // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest/1916122-supportednetworks
      const supportedNetworks: string[] = ["visa", "masterCard", "amex"];
      if (supportedVersion >= 4) {
        supportedNetworks.push("cartesBancaires");
        supportedNetworks.push("maestro");
        supportedNetworks.push("electron");
        supportedNetworks.push("vPay");
        if (supportedVersion >= 5) {
          supportedNetworks.push("mada");
        }
      }

      let tempPartPaymentRequest: Pick<ApplePayPaymentRequest, "currencyCode" | "total" | "lineItems">;
      if (place === "cart") {
        if (!isCart(context)) {
          // Should never be the case
          throw Error("No cart context in cart on Apple Pay loadData");
        }
        const {cart} = context;
        tempPartPaymentRequest = {
          currencyCode: cart.currencyCode,
          total: {
            label: t("cart.total"),
            amount: cart.valuePayable.amount,
          },
          lineItems: positionsToLineItems(cart),
        };
      } else {
        const productData = getProductData();
        const datasetItemId = dbqs<HTMLElement>("#AddToCartButtonRoot")?.itemid;
        if (!productData || !datasetItemId) {
          return;
        }

        setItemId(Number(datasetItemId));

        tempPartPaymentRequest = {
          currencyCode: window.c_ActiveCurrency as CurrencyCode,
          total: {
            label: t("cart.total"),
            amount: productData.totalAmount,
          },
          lineItems: productData.lineItems,
        };
      }

      const tempPaymentRequest: Omit<ApplePayPaymentRequest, "merchantCapabilities"> = {
        countryCode: isoLocales[getShopId()].split("_")[1],
        requiredBillingContactFields: ["email", "name", "postalAddress"],
        requiredShippingContactFields: ["email", "name", "postalAddress"],
        supportedCountries: context?.countries.map((cntry) => cntry.countryCode) ?? [],
        shippingContactEditingMode: "available",
        supportsCouponCode: true,
        supportedNetworks,
        ...tempPartPaymentRequest,
      };

      const applePayClient = await braintree.applePay.create({client});
      const paymentRequest = applePayClient.createPaymentRequest(tempPaymentRequest);
      setPaymentData({paymentRequest, applePayClient});
    }
    void loadData(context.braintreeData.client);
  }, [context]);

  /**
   * Mounts the script tag, that loads the Apple Pay js, that
   * captures the already mounted `<apple-pay-button>` tag
   */
  useEffect(() => {
    if (!paymentData) {
      return;
    }
    void loadScript({
      crossorigin: true,
      src: "https://applepay.cdn-apple.com/jsapi/v1.1.0/apple-pay-sdk.js",
    }).then(() => setScriptLoaded(true));

    return () => loadScript.clearCache();
  }, [paymentData]);

  const doErrorMagic = useCallback(
    (
      error: braintree.BraintreeError | Error,
      errorNamePrefix: string,
      additional?: Record<string, ErrorMagicAdditionalType | undefined> | {validationURL: string},
    ) => {
      try {
        error.name = errorNamePrefix + error.name;
      } catch (e) {
        // eslint-disable-next-line no-console
        if (__DEVELOPMENT__) console.log("DEVELOP: Non-renamable error:", (e as Error).name);
      }
      const {errorArray, sentryEventId} = expressPaymentsError(error, "Apple Pay", {...additional, supportedVersion});
      const cartContext = contextRef.current;
      if (!cartContext) return;

      if (isCart(cartContext)) {
        cartContext.setExternalErrors(
          errorArray.map((err) => ({
            message: t(err, t(DEFAULT_ERROR_MESSAGE, {sentryEventId})),
            blockCheckout: false,
          })),
        );

        if (!cartContext.isCartVisible) cartContext.setIsCartVisible(true);
      }

      void checkoutRef.current?.put({paymentId: null});
    },
    [paymentData],
  );

  if (!paymentData) {
    return (
      <div className="apple-pay-button-container">
        <div className="spinner-container">
          <PayPalLoadingSpinner />
        </div>
      </div>
    );
  }

  const onApplePayButtonClicked = () => {
    if (isBlockedRef.current) {
      return;
    }

    if (!paymentDataRef.current) {
      doErrorMagic(new Error("No 'paymentDataRef.current' at beginning of click handler"), "NoRef");

      return;
    }

    try {
      const session = new ApplePaySession(supportedVersion, paymentDataRef.current.paymentRequest);

      // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778021-onvalidatemerchant
      session.onvalidatemerchant = (event: ApplePayValidateMerchantEvent) => {
        console.log("Merchant validation started");
        /* eslint-disable @typescript-eslint/no-unsafe-assignment */
        let validationResultPromise;
        const validateRefs = async () => {
          if (!paymentDataRef.current) throw Error("No `paymentDataRef.current` on merchant validation");
          if (!contextRef.current) throw Error("contextRef.current empty!");

          const cartAndCheckoutPromise = decideCartContent(place, contextRef.current, itemId).then(async (cart) => {
            const checkout = await retrieveCheckout(cart, Payments.ApplePay);

            return {cart, checkout};
          });

          // this 'await' seems odd, but it's necessary, bc the `validateRefs().catch(...)`
          // would actually run, but wouldn't execute the `doErrorMagic()`
          validationResultPromise = await paymentDataRef.current.applePayClient.performValidation({
            validationURL: event.validationURL,
            displayName,
          });

          await Promise.all([validationResultPromise, cartAndCheckoutPromise]).then(
            ([validationResult, cartAndCheckout]) => {
              cartRef.current = cartAndCheckout.cart;
              checkoutRef.current = cartAndCheckout.checkout;

              console.log("completeMerchantValidation");
              session.completeMerchantValidation(validationResult);
            },
          );
        };

        void validateRefs().catch(async (e) => {
          console.log("Merchant validation error:", e);
          doErrorMagic(e as Error, INTERNAL_ERROR_TYPE.APPLE_PAY.OnValidation, {
            event,
            validationResult: await validationResultPromise,
            validationURL: event.validationURL,
          });
        });
        /* eslint-enable @typescript-eslint/no-unsafe-assignment */
      };

      // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778009-onshippingcontactselected
      session.onshippingcontactselected = (event: ApplePayShippingContactSelectedEvent) => {
        // A little bit more complicated. It does a bit more initializing magic
        void asyncPutShippingCountry({
          shippingContact: event.shippingContact,
          cart: cartRef.current,
          checkout: checkoutRef.current,
          session,
          shippingMethods: shippingMethodsRef.current,
        })
          .then((shippingMethods: ApplePayShippingMethod[] | undefined) => {
            shippingMethodsRef.current = shippingMethods;
          })
          .catch((e) => {
            console.log("shipping country selection error:", e);
            doErrorMagic(e as Error, INTERNAL_ERROR_TYPE.APPLE_PAY.OnShippingContactSelect, {event});
          });
      };

      // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778028-onshippingmethodselected
      session.onshippingmethodselected = (event: ApplePayShippingMethodSelectedEvent) => {
        void asyncPutShippingMethod(event, checkoutRef.current, session).catch((e) => {
          console.log("shipping method selection error:", e);
          doErrorMagic(e as Error, INTERNAL_ERROR_TYPE.APPLE_PAY.OnShippingMethodSelect, {event});
        });
      };

      // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778013-onpaymentmethodselected
      session.onpaymentmethodselected = (event: ApplePayPaymentMethodSelectedEvent) => {
        void putPaymentMethod(checkoutRef.current, session).catch((err) => {
          console.log("payment method selection error:", err);
          doErrorMagic(err as Error, INTERNAL_ERROR_TYPE.APPLE_PAY.OnPaymentSelect, {event});
        });
      };

      // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/3813982-oncouponcodechanged
      session.oncouponcodechanged = (event: ApplePayCouponCodeChangedEvent) => {
        void asyncCouponCodeChange(event, session, cartRef.current).catch((e) => {
          doErrorMagic(e as Error, INTERNAL_ERROR_TYPE.APPLE_PAY.OnCouponSelect, {event});
        });
      };

      // https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778020-onpaymentauthorized
      // node_modules/braintree-web/apple-pay/apple-pay.js:305
      session.onpaymentauthorized = (event: ApplePayPaymentAuthorizedEvent) => {
        const errorParamsArray = findAuthorizeErrors(event, paymentDataRef.current);
        if (errorParamsArray.length) {
          session.completePayment({
            status: ApplePaySession.STATUS_FAILURE,
            errors: errorParamsArray.map((errorParams) => new ApplePayError(...errorParams)),
          });

          return;
        }

        const {billingContact, shippingContact} = event.payment;

        if (shippingContact && billingContact && checkoutRef.current) {
          const email = shippingContact.emailAddress || billingContact.emailAddress || "";
          let delivery: IAddressData;
          let invoice: IAddressData;
          let addressType: ApplePayJS.ApplePayErrorCode = "shippingContactInvalid";
          try {
            delivery = appleAddressAdapter(shippingContact);
            addressType = "billingContactInvalid";
            invoice = {...appleAddressAdapter(billingContact), email};
            if (__DEVELOPMENT__) {
              console.log("onPaymentAutorized delivery:", delivery);
              console.log("onPaymentAutorized invoice:", invoice);
            }
          } catch (e) {
            session.completePayment({
              status: ApplePaySession.STATUS_FAILURE,
              errors: [new ApplePayError(addressType, "addressLines", t((e as Error).message))],
            });

            return;
          }

          void checkoutRef.current
            .put({
              address: {
                delivery,
                invoice,
              },
              paymentId: Payments.ApplePay,
            })
            .then(() => {
              const invoiceMessages = (checkoutRef.current?.invoiceAddress?.messages || []).filter(
                (msg) => msg.type === "error",
              ).map((deliveryMessage) => {
                return new ApplePayError("billingContactInvalid", "addressLines", t(deliveryMessage.message));
              });
              const deliveryMessages = (checkoutRef.current?.deliveryAddress?.messages || []).filter(
                (msg) => msg.type === "error" && msg.field !== "phone",
              ).map((deliveryMessage) => {
                return new ApplePayError("shippingContactInvalid", "addressLines", t(deliveryMessage.message));
              });
              const allMessages = invoiceMessages.concat(deliveryMessages);
              if (allMessages.length) {
                session.completePayment({
                  status: ApplePaySession.STATUS_FAILURE,
                  errors: allMessages,
                });

                return;
              }

              if (!paymentDataRef.current) return;
              void paymentDataRef.current.applePayClient
                .tokenize({token: event.payment.token})
                .then(({nonce}) => {
                  session.completePayment({status: ApplePaySession.STATUS_SUCCESS});
                  console.log("Payment authorization completed");
                  Exponea.trackPdpPurchase();

                  setAPayDataOnViewState(nonce, contextRef.current || null, place);
                  if (typeof contextRef.current !== "undefined" && isCart(contextRef.current)) {
                    contextRef.current.setIsCartVisible(true);
                    contextRef.current.setIsIntoCheckout(true);
                    updateUrlParams("checkout");
                  }
                })
                .catch((e) => {
                  doErrorMagic(e as Error, INTERNAL_ERROR_TYPE.APPLE_PAY.OnAuthorize, {
                    event,
                  });

                  session.completePayment({
                    status: ApplePaySession.STATUS_FAILURE,
                    errors: [new ApplePayError("unknown", undefined, "Error on payment tokenization")],
                  });
                });
            });
        }
      };

      session.oncancel = (event) => {
        if (event.type !== "cancel") console.log(event);
        // no sync execution allowed
        const restoreCartOnCancel = async () => {
          if (cartRef.current) await restoreCart(cartRef.current);
          await checkoutRef.current?.put({paymentId: null});
        };
        void restoreCartOnCancel();
      };

      // eslint-disable-next-line no-console
      console.log("begin session");
      session.begin();
    } catch (err) {
      doErrorMagic(err as Error, INTERNAL_ERROR_TYPE.APPLE_PAY.OnInit);
    }
  };

  if (elemRef.current && !elemRef.current.onclick) {
    // Has to be assigned on the native element, because of the
    // differing React behaviour related to `onClick` attributes
    elemRef.current.onclick = onApplePayButtonClicked;
  }

  return (
    <div className={`apple-pay-button-container${isBlocked ? " blocked" : ""}`}>
      <apple-pay-button
        ref={elemRef}
        buttonstyle="white-outline"
        type="continue"
        locale={isoLocales[getShopId()].replace("_", "-")}
      ></apple-pay-button>
    </div>
  );
};

export default ApplePayButtonLoader;
